import { get, post, put, del } from '../../../../GlobalModules/Utility/Http';
import {
  storeListGet,
  storeDetailsGet,
  storeCreatePost,
  storeUpdatePut,
  storeDeleteDel,
  storeStatusPut,
  genericApiGet
} from '../Model/StoreModel';

/**
 * Get a paginated list of stores with optional filtering and sorting
 * @param {Object} filters - Filter parameters for the API call
 * @returns {Promise<Object>} - Processed response with store list
 */
export const getStoreList = async (filters = {}) => {
  // Format filters according to the API structure
  let queryParams = {};

  if (filters) {
    if (filters.all) {
      queryParams.all = true;
    }
    // Handle search term
    if (filters.search) {
      queryParams.filter_op = JSON.stringify({ search_attr: "substring" });
      queryParams.filters = JSON.stringify({
        limit: filters.limit || 10,
        offset: filters.offset || 0,
        search_attr: filters.search
      });
    } else {
      // Handle pagination and sorting
      const filtersObj = {
        limit: filters.limit || 10,
        offset: filters.offset || 0
      };

      // Add status filter if provided
      if (filters.status !== undefined && filters.status !== '') {
        filtersObj.status = filters.status;
      }

      // Add sorting if provided
      if (filters.sort && filters.sort.length > 0) {
        filtersObj.sort = filters.sort.map(item => ({
          colId: item.field,
          sort: item.sort
        }));
      }
      queryParams.filters = JSON.stringify(filtersObj);
    }
  }
  const response = await get(`${process.env.EXTENDED_URL}/store`, queryParams);
  // Ensure we're getting the total count from the response
  let processedResponse = storeListGet(response);

  // If the API response has a total field but our processed response doesn't have total_count
  if (response?.data?.total && !processedResponse.total_count) {
    processedResponse.total_count = response.data.total;
  }

  return processedResponse;
};

/**
 * Get details of a specific store
 * @param {string} slack - Store identifier
 * @returns {Promise<Object>} - Processed response with store details
 */
export const getStoreDetails = async (slack) => {
  const response = await get(`${process.env.EXTENDED_URL}/store/${slack}`);
  return storeDetailsGet(response);
};

/**
 * Create a new store
 * @param {Object} storeData - Store data to create
 * @returns {Promise<Object>} - Response from API
 */
export const createStore = async (storeData) => {
  const apiData = {
    // Basic information
    name: storeData.name || '',
    store_code: storeData.store_code || '',
    tax_number: storeData.tax_number || '',

    // Contact information
    primary_contact: storeData.primary_contact || '',
    secondary_contact: storeData.secondary_contact || '',
    primary_email: storeData.primary_email || '',
    secondary_email: storeData.secondary_email || '',
    store_type_id: storeData.store_type_id || '',
    store_licence: storeData.store_licence || '',
    receipt_note: storeData.receipt_note || '',
    // Address information
    address: storeData.address || '',
    pincode: storeData.pincode || '',
    country_id: storeData.country || '',
    state: storeData.state || '',
    state_code: storeData.state_code || '',

    // Status
    status: storeData.status === "active" ? 1 : 0,

    // Restaurant mode settings
    restaurant_mode: storeData.restaurant_mode || 0,
    restaurant_billing_type_id: storeData.restaurant_billing_type || '',
    restaurant_waiter_role_id: storeData.restaurant_waiter_role || '',
    restaurant_chef_role_id: storeData.restaurant_chef_role || '',

    // Digital menu settings
    digital_menu_enabled: storeData.digital_menu_enabled ? 1 : 0,
    menu_open_time: storeData.menu_open_time || '',
    menu_close_time: storeData.menu_close_time || '',
    enable_digital_menu_otp_verification: storeData.enable_digital_menu_otp_verification || 0,
    digital_menu_send_order_to_kitchen: storeData.digital_menu_send_order_to_kitchen || 0,
    menu_language_id: storeData.menu_language || '',

    // Currency and tax settings
    currency_code: storeData.currency_code || '',
    tax_code_id: '',
    discount_code_id: '',

    // POS screen settings
    enable_customer_popup: storeData.enable_customer_popup || 0,
    enable_variants_popup: storeData.enable_variants_popup || 0,

    // Invoice settings
    invoice_type: storeData.invoice_type || '',

    // Print settings
    printnode_enabled: storeData.printnode_enabled ? 1 : 0,
    printnode_api_key: storeData.printnode_api_key || '',
    pos_printer_id: storeData.pos_invoice_printer || '',
    // kot_printer_id: storeData.kot_printer || '',
    other_printer_id: storeData.other_printer || '',

    invoice_format: storeData.invoice_format || '',
    invoice_sequence_digits: storeData.invoice_sequence_digits || '',
    invoice_start_number: storeData.invoice_start_number || '',
    invoice_reset_sequence: storeData.invoice_reset_sequence || '',

    kot_printer_id: storeData.kot_printer_id || '',
    bill_printer_id: storeData.bill_printer_id || '',

    max_business_hours: String(storeData.max_business_hours || ''),

    is_primary: storeData.is_primary || false,
    parent_store_id: storeData.parent_store_id || null,
    store_type: storeData.store_type || '',
    is_procurement_hub: storeData.is_procurement_hub || false,
    self_procurement: storeData.self_procurement || false,
    can_sell: storeData.can_sell || false,
    is_pms_connected: storeData.is_pms_connected || false,
    order_type_constants: storeData.order_type_constants || [],
    pms_integration_enabled: storeData.pms_integration_enabled ? 1 : 0,
    bar_enabled: storeData.bar_enabled ? 1 : 0,
  };

  const response = await post(`${process.env.EXTENDED_URL}/store`, apiData);
  return storeCreatePost(response);
};

/**
 * Update an existing store
 * @param {string} slack - Store slack to update
 * @param {Object} storeData - Updated store data
 * @returns {Promise<Object>} - Response from API
 */
export const updateStore = async (slack, storeData) => {
  const apiData = {
    // Basic information
    name: storeData.name || '',
    store_code: storeData.store_code || '',
    tax_number: storeData.tax_number || '',
    // store_type_id:storeData.store_type_id || '',
    store_licence: storeData.store_licence || '',
    receipt_note: storeData.receipt_note || '',

    // Contact information
    primary_contact: storeData.primary_contact || '',
    secondary_contact: storeData.secondary_contact || '',
    primary_email: storeData.primary_email || '',
    secondary_email: storeData.secondary_email || '',

    // Address information
    address: storeData.address || '',
    pincode: storeData.pincode || '',
    country_id: storeData.country || '',
    state: storeData.state || '',
    state_code: storeData.state_code || '',

    // Status
    status: storeData.status === "active" ? 1 : 0,

    // Restaurant mode settings
    restaurant_mode: storeData.restaurant_mode || 0,
    restaurant_billing_type_id: storeData.restaurant_billing_type || '',
    restaurant_waiter_role_id: storeData.restaurant_waiter_role || '',
    restaurant_chef_role_id: storeData.restaurant_chef_role || '',

    // Digital menu settings
    digital_menu_enabled: storeData.digital_menu_enabled ? 1 : 0,
    menu_open_time: storeData.menu_open_time || '',
    menu_close_time: storeData.menu_close_time || '',
    enable_digital_menu_otp_verification: storeData.enable_digital_menu_otp_verification || 0,
    digital_menu_send_order_to_kitchen: storeData.digital_menu_send_order_to_kitchen || 0,
    menu_language_id: storeData.menu_language || '',

    // Currency and tax settings
    currency_code: storeData.currency_code || '',
    tax_code_id: storeData.tax_code_id || '',
    discount_code_id: storeData.discount_code_id || '',

    // POS screen settings
    enable_customer_popup: storeData.enable_customer_popup || 0,
    enable_variants_popup: storeData.enable_variants_popup || 0,

    // Invoice settings
    invoice_type: storeData.invoice_type || '',

    // Print settings
    printnode_enabled: storeData.printnode_enabled ? 1 : 0,
    printnode_api_key: storeData.printnode_api_key || '',
    pos_printer_id: storeData.pos_invoice_printer || '',
    // kot_printer_id: storeData.kot_printer || '',
    other_printer_id: storeData.other_printer || '',

    invoice_format: storeData.invoice_format || '',
    invoice_sequence_digits: storeData.invoice_sequence_digits || '',
    invoice_start_number: storeData.invoice_start_number || '',
    invoice_reset_sequence: storeData.invoice_reset_sequence || '',

    kot_printer_id: storeData.kot_printer_id || '',
    bill_printer_id: storeData.bill_printer_id || '',

    max_business_hours: String(storeData.max_business_hours || ''),
    is_primary: storeData.is_primary ? true : false,

    parent_store_id: storeData.parent_store_id || '',
    store_type: storeData.store_type || '',
    is_procurement_hub: storeData.is_procurement_hub || false,
    self_procurement: storeData.self_procurement || false,
    can_sell: storeData.can_sell || false,
    is_pms_connected: storeData.is_pms_connected || false,
    order_type_constants: storeData.order_type_constants || [],
    pms_integration_enabled: storeData.pms_integration_enabled ? 1 : 0,
    bar_enabled: storeData.bar_enabled ? 1 : 0,
  };

  if (storeData.store_type === "MAIN") {
  } else {
    apiData.store_type_id = storeData.store_type_id || ''
  }
  // console.log("API DATA", apiData);
  const response = await put(`${process.env.EXTENDED_URL}/store/${slack}`, apiData);
  return storeUpdatePut(response);
};

/**
 * Delete a store
 * @param {string} slack - Store identifier
 * @returns {Promise<Object>} - Processed response
 */
export const deleteStore = async (slack) => {
  const response = await del(`${process.env.EXTENDED_URL}/store/${slack}`);
  return storeDeleteDel(response);
};

/**
 * Update store status
 * @param {string} slack - Store identifier
 * @param {number} status - New status (1 for active, 0 for inactive)
 * @returns {Promise<Object>} - Processed response
 */
export const updateStoreStatus = async (slack, status) => {
  const apiData = {
    status: status === "active" ? 1 : 0
  };

  const response = await put(`${process.env.EXTENDED_URL}/store/${slack}/status`, apiData);
  return storeStatusPut(response);
};

/**
 * Export stores to CSV
 * @param {Object} filters - Filter parameters for the export
 * @returns {Promise<Object>} - Processed response with export URL
 */
export const exportStores = async (filters = {}) => {
  try {
    // Format filters according to the API structure
    let queryParams = {};

    if (filters) {
      // Handle search term
      if (filters.search) {
        queryParams.filter_op = JSON.stringify({ search_attr: "substring" });
        queryParams.filters = JSON.stringify({
          search_attr: filters.search,
          status: filters.status || ''
        });
      } else {
        // Handle filters
        const filtersObj = {};

        // Add status filter if provided
        if (filters.status !== undefined && filters.status !== '') {
          filtersObj.status = filters.status;
        }

        // Add sorting if provided
        if (filters.sort && filters.sort.length > 0) {
          filtersObj.sort = filters.sort.map(item => ({
            colId: item.field,
            sort: item.sort
          }));
        }

        queryParams.filters = JSON.stringify(filtersObj);
      }
    }

    const response = await post(`${process.env.EXTENDED_URL}/report/store`, queryParams);
    return response;
  } catch (error) {
    console.error('Error exporting stores:', error);
    throw error;
  }
};

/**
 * Get list of countries for dropdown
 * @returns {Promise<Object>} - Object with success status and country data
 */
export const getCountries = async () => {
  try {
    const params = {
      filters: JSON.stringify([{
        "resource_name": "Country",
        "columns": ["id", "name", "code", "dial_code", "currency_name", "currency_code"],
        "column_filters": []
      }])
    };

    const response = await get(`${process.env.EXTENDED_URL_WITHOUT_VERSION}/generic/`, params);
    if (response.data &&
      response.data.success &&
      response.data.data &&
      response.data.data.length > 0 &&
      response.data.data[0].Country_data) {
      return {
        success: true,
        data: response.data.data[0].Country_data || [],
        total: response.data.data[0].total || 0
      };
    }

    return {
      success: false,
      data: [],
      message: 'No countries found'
    };
  } catch (error) {
    console.error('Error fetching countries:', error);
    return {
      success: false,
      message: 'Failed to fetch countries',
      data: []
    };
  }
};

/**
 * Fetch store types for dropdown
 * @returns {Promise} List of store types
 */
export const getStoreTypes = async () => {
  try {
    const filters = JSON.stringify([
      {
        resource_name: "MasterStoreType",
        columns: ["id", "store_type_name", "description"],
        column_filters: [],
      },
    ]);

    const response = await get(
      `${process.env.EXTENDED_URL_WITHOUT_VERSION}generic/`,
      { filters }
    );

    return genericApiGet(response, "MasterStoreType");
  } catch (error) {
    console.error("Error fetching store types:", error);
    return {
      success: false,
      message: "Failed to fetch store types",
      data: [],
    };
  }
};

/**
 * Fetch active order types
 * @returns {Promise<Object>} - List of order types
 */
export const getOrderTypes = async () => {
  try {
    const filters = JSON.stringify([
      {
        resource_name: "MasterOrderType",
        columns: ["id", "order_type_constant", "label", "icon"],
        column_filters: [{ status: 1 }],
      },
    ]);

    const response = await get(
      `${process.env.EXTENDED_URL_WITHOUT_VERSION}generic/`,
      { filters }
    );

    return genericApiGet(response, "MasterOrderType");
  } catch (error) {
    console.error("Error fetching order types:", error);
    return {
      success: false,
      message: "Failed to fetch order types",
      data: [],
    };
  }
};


/**
 * Get a list of billing types from the generic API
 * @returns {Promise<Object>} - List of billing types
 */
export const getBillingTypes = async () => {
  try {
    const params = {
      filters: JSON.stringify([{
        resource_name: "MasterBillingType",
        columns: ["id", "billing_type_constant", "label"],
        column_filters: []
      }])
    };

    const response = await get(`${process.env.EXTENDED_URL_WITHOUT_VERSION}/generic/`, params);

    if (response.data &&
      response.data.success &&
      response.data.data &&
      response.data.data.length > 0 &&
      response.data.data[0].MasterBillingType_data) {
      return {
        success: true,
        data: response.data.data[0].MasterBillingType_data || [],
        total: response.data.data[0].total || 0
      };
    }

    return {
      success: false,
      data: [],
      message: 'No billing types found'
    };
  } catch (error) {
    console.error('Error fetching billing types:', error);
    return {
      success: false,
      message: 'Failed to fetch billing types',
      data: []
    };
  }
};

/**
 * Get a list of invoice print types from the generic API
 * @returns {Promise<Object>} - List of invoice print types
 */
export const getInvoicePrintTypes = async () => {
  try {
    const params = {
      filters: JSON.stringify([{
        resource_name: "MasterInvoicePrintType",
        columns: ["id", "print_type_value", "print_type_label"],
        column_filters: []
      }])
    };

    const response = await get(`${process.env.EXTENDED_URL_WITHOUT_VERSION}/generic/`, params);

    if (response.data &&
      response.data.success &&
      response.data.data &&
      response.data.data.length > 0 &&
      response.data.data[0].MasterInvoicePrintType_data) {
      return {
        success: true,
        data: response.data.data[0].MasterInvoicePrintType_data || [],
        total: response.data.data[0].total || 0
      };
    }

    return {
      success: false,
      data: [],
      message: 'No invoice print types found'
    };
  } catch (error) {
    console.error('Error fetching invoice print types:', error);
    return {
      success: false,
      message: 'Failed to fetch invoice print types',
      data: []
    };
  }
};

/**
 * Get a list of restaurant roles from the generic API
 * @returns {Promise<Object>} - List of restaurant roles
 */
export const getRestaurantRoles = async (branchId) => {
  try {
    const params = {
      filters: JSON.stringify([{
        resource_name: "Role",
        columns: ["id", "name", "branch_id"],
        column_filters: [{ branch_id: branchId || localStorage.getItem('store_id') }]
      }])
    };

    const response = await get(`${process.env.EXTENDED_URL_WITHOUT_VERSION}/generic/`, params);

    if (response.data &&
      response.data.success &&
      response.data.data &&
      response.data.data.length > 0 &&
      response.data.data[0].Role_data) {
      return {
        success: true,
        data: response.data.data[0].Role_data || [],
        total: response.data.data[0].total || 0
      };
    }

    return {
      success: false,
      data: [],
      message: 'No restaurant roles found'
    };
  } catch (error) {
    console.error('Error fetching restaurant roles:', error);
    return {
      success: false,
      message: 'Failed to fetch restaurant roles',
      data: []
    };
  }
};

/**
 * Get a list of menu languages from the generic API
 * @returns {Promise<Object>} - List of menu languages
 */
export const getMenuLanguages = async () => {
  try {
    const params = {
      filters: JSON.stringify([{
        resource_name: "Language",
        columns: ["id", "language_code", "language_constant", "language"],
        column_filters: []
      }])
    };

    const response = await get(`${process.env.EXTENDED_URL_WITHOUT_VERSION}/generic/`, params);

    if (response.data &&
      response.data.success &&
      response.data.data &&
      response.data.data.length > 0 &&
      response.data.data[0].Language_data) {
      return {
        success: true,
        data: response.data.data[0].Language_data || [],
        total: response.data.data[0].total || 0
      };
    }

    return {
      success: false,
      data: [],
      message: 'No menu languages found'
    };
  } catch (error) {
    console.error('Error fetching menu languages:', error);
    return {
      success: false,
      message: 'Failed to fetch menu languages',
      data: []
    };
  }
};

/**
 * Get available printers from PrintNode API
 * @param {string} apiKey - PrintNode API key
 * @returns {Promise<Object>} - Processed response with printer list
 */
export const getPrinters = async (apiKey) => {
  try {
    const params = {
      filters: JSON.stringify([{
        "resource_name": "Printer",
        "columns": ["id", "printer_code", "printer_name", "printer_id", "store_id"],
        "column_filters": [{ "store_id": localStorage.getItem('store_id') }]
      }])
    };

    const response = await get(`${process.env.EXTENDED_URL_WITHOUT_VERSION}/generic/`, params);

    if (response.data &&
      response.data.success &&
      response.data.data &&
      response.data.data.length > 0 &&
      response.data.data[0].Printer_data) {
      return {
        success: true,
        data: response.data.data[0].Printer_data || [],
        total: response.data.data[0].total || 0
      };
    }

    return {
      success: false,
      data: [],
      message: 'No printers found'
    };
  } catch (error) {
    console.error('Error fetching printers:', error);
    return {
      success: false,
      message: 'Failed to fetch printers',
      data: []
    };
  }
};

/**
 * Get available tax codes from taxcode API
 * @param {string} apiKey - taxcode API key
 * @returns {Promise<Object>} - Processed response with tax codes list
 */
export const getTaxCodes = async (storeId) => {
  try {
    let queryParams = {};
    queryParams.filters = JSON.stringify({
      store_id: storeId || localStorage.getItem('store_id'),
      "status": 1
    });

    const response = await get(`${process.env.EXTENDED_URL}/taxcode/list`, queryParams);
    if (response.data &&
      response.data.success &&
      response.data.data &&
      response.data.data.length > 0) {
      return {
        success: true,
        data: response.data.data || [],
        total: response.data.total || 0
      };
    } else {
      return {
        success: true,
        data: [],
        // message: 'No tax codes found'
      };
    }
  } catch (error) {
    console.error('Error fetching tax codes:', error);
    return {
      success: false,
      message: 'Failed to fetch tax codes',
      data: []
    };
  }
};

/**
 * Get available tax codes from discountcode API
 * @param {string} apiKey - discountcode API key
 * @returns {Promise<Object>} - Processed response with discount code list
 */
export const getDiscountCodes = async (storeId) => {
  try {
    let queryParams = {};
    queryParams.filters = JSON.stringify({
      store_id: storeId || localStorage.getItem('store_id'),
      "status": 1
    });

    const response = await get(`${process.env.EXTENDED_URL}/discountcode`, queryParams);
    if (response.data &&
      response.data.success &&
      response.data.data &&
      response.data.data.length > 0) {
      return {
        success: true,
        data: response.data.data || [],
        total: response.data.total || 0
      };
    } else {
      return {
        success: true,
        data: [],
        // message: 'No discount codes found'
      };
    }
  } catch (error) {
    console.error('Error fetching discount codes:', error);
    return {
      success: false,
      message: 'Failed to fetch discount codes',
      data: []
    };
  }
};

/**
 * Get available tax codes from discountcode API
 * @param {string} apiKey - discountcode API key
 * @returns {Promise<Object>} - Processed response with discount code list
 */
export const getMasterInvoice = async (printType = "BILL") => {
  try {
    let queryParams = {};
    queryParams.filters = JSON.stringify({
      store_id: localStorage.getItem('store_id'),
      "status": 1,
      "print_type": printType
    });

    const response = await get(`${process.env.EXTENDED_URL}/master_invoice_print_type`, queryParams);
    if (response.data &&
      response.data.success &&
      response.data.data &&
      response.data.data.length > 0) {
      return {
        success: true,
        data: response.data.data || [],
        total: response.data.total || 0
      };
    } else {
      return {
        success: true,
        data: [],
        // message: 'No tax codes found'
      };
    }
  } catch (error) {
    console.error('Error fetching tax codes:', error);
    return {
      success: false,
      message: 'Failed to fetch tax codes',
      data: []
    };
  }
};

/**
 * Get store operating hours
 * @param {number} storeId - Store ID
 * @returns {Promise<Object>} - Processed response with operating hours data
 */
export const getStoreOperatingHours = async (storeId) => {
  try {
    const queryParams = {
      filters: JSON.stringify({ store_id: storeId })
    };

    const response = await get(`${process.env.EXTENDED_URL}/master_business_time`, queryParams);

    if (response.data.success && response.data.data) {
      return {
        success: true,
        data: response.data.data || [],
        total: response.total || 0
      };
    } else {
      return {
        success: false,
        data: [],
        message: response.message || 'Failed to fetch operating hours'
      };
    }
  } catch (error) {
    console.error('Error fetching operating hours:', error);
    return {
      success: false,
      message: 'Failed to fetch operating hours',
      data: []
    };
  }
};

/**
 * Save store operating hours
 * @param {Object} operatingHoursData - Operating hours data to save
 * @returns {Promise<Object>} - Processed response
 */
export const saveStoreOperatingHours = async (operatingHoursData) => {
  try {
    const payload = {
      store_id: operatingHoursData.store_id,
      weekday_number: operatingHoursData.weekday_number,
      is_active: operatingHoursData.is_active,
      open_time: operatingHoursData.open_time,
      close_time: operatingHoursData.close_time
    };

    let response;

    // If slack exists, use PUT method to update existing record
    if (operatingHoursData.slack) {
      response = await put(`${process.env.EXTENDED_URL}/master_business_time/${operatingHoursData.slack}`, payload);
    } else {
      // Otherwise use POST method to create new record
      response = await post(`${process.env.EXTENDED_URL}/master_business_time/create`, payload);
    }
    if (response.data.success) {
      return {
        success: true,
        data: response.data.data,
        message: 'Operating hours saved successfully'
      };
    } else {
      return {
        success: false,
        message: response.message || 'Failed to save operating hours'
      };
    }
  } catch (error) {
    console.error('Error saving operating hours:', error);
    return {
      success: false,
      message: 'Failed to save operating hours'
    };
  }
};